import { FormikValues } from 'formik';
import { useMemo, useSyncExternalStore } from 'react';
import { dotToObject, mergeDeep } from './utils';
import deepEqual from 'deep-equal';
import { isInput, isSelect } from './dom';

/**
 *
 * This is an optimized hook for formdata replacing: { values } useFormikContext().
 * It is only updating, when query result is changing, as opposed to FormikContext is updating on evry update for all fields all the time.
 * @param formName Name of form to subscribe to
 * @param query Function transforming formdata to value
 * @param defaults Default values also provided to Formik
 * @returns result from query on formdata
 */
export function useFormDataQuery<TFormData extends FormikValues, TReturn>(
    formName: string,
    query: (data: TFormData) => TReturn,
    defaults: TFormData,
    debug?: string
) {
    const store = useMemo(() => {
        const formdata = mergeDeep(defaults, dotToObject(getFormData(formName))) as TFormData;
        let cachedQuery = query(formdata);
        debug && console.log(`Cache content for ${debug}:`, cachedQuery);

        const subscribe = (callback: () => void) => {
            const onChange = () => {
                const data = mergeDeep(defaults, dotToObject(getFormData(formName))) as TFormData;
                const queryResult = query(data);

                if (!deepEqual(queryResult, cachedQuery)) {
                    debug && console.log('query changed', cachedQuery, queryResult);
                    cachedQuery = queryResult;
                    callback();
                }
            };

            document.addEventListener('change', onChange);
            return () => {
                document.addEventListener('change', onChange);
            };
        };

        const getSnapshot = () => cachedQuery;

        return { subscribe, getSnapshot };
    }, [debug, defaults, formName, query]);

    return useSyncExternalStore(store.subscribe, store.getSnapshot);
}
function getFormData(formName: string) {
    const selector = `form[name='${formName}'] input[name],form[name='${formName}'] select[name]`;

    return Object.fromEntries(
        Array.from(document.querySelectorAll(selector).values())
            .filter((elm) => !isInput(elm) || elm.type != 'radio' || elm.checked)
            .map((elm) => {
                return [
                    elm['name' as keyof typeof elm],
                    isInput(elm) && elm.type == 'checkbox'
                        ? elm.checked
                        : isSelect(elm)
                        ? elm.options[elm.selectedIndex].value
                        : elm['value' as keyof typeof elm],
                ];
            })
    );
}
